import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import _intersection from 'lodash/intersection';
import qs from 'qs';

const LOCAL_STORE_PREFIX = process.env.VUE_APP_LOCAL_STORE_PREFIX;
const API_URL = process.env.VUE_APP_API_URL;
const CLAVEUNICA_LOGOUT_URL = process.env.VUE_APP_MINSAL_API_REFERENCE_URL + '/logout';

const minsalAxios = axios.create({
  baseURL: API_URL
});

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    user: null,
    localToken: localStorage.getItem(`${LOCAL_STORE_PREFIX}-user-token`),
    windowSize: { innerWidth: window.innerWidth, innerHeight: window.innerHeight }
  },
  getters: {
    windowWidth(state) {
      return state.windowSize.innerWidth;
    },
    user(state) {
      return state.user;
    },
    loggedIn(state) {
      return state.user !== null;
    },
    hasSavedSession(state) {
      return state.localToken !== null;
    },
    defaultOrganization(state) {
      return state.user.organizations.length ? state.user.organizations[0] : undefined;
    },
    hasRole: (state) => (role, org = null) => {
      let orgbool = true;
      if (org && state.user.type == 'dispenser_admin') {
        orgbool = false;
        state.user.organizations.forEach(element => {
          if (element.uuid == org) {
            orgbool = true;
          }
        });
      }

      return orgbool && _intersection(role.split('|'), state.user.type.split('|')).length > 0;
    }
  },
  actions: {
    async processImport({ commit }, data) {
      return minsalAxios.post(`/import/process`, data)
        .then(response => response.data);
    },
    async getUserInfo() {
      return minsalAxios.get(`/me`);
    },
    async saveUser({ commit }, formData) {
      return minsalAxios.post(`/users`, formData)
        .then(response => response.data);
    },
    async fetchUser(context, params) {
      return minsalAxios.get(`/users/${params.id}`)
        .then(response => response.data);
    },
    async fetchUserDetails(context, params) {
      return minsalAxios.get(`/users/${params.rut}/user-details?type=${params.type}`)
        .then(response => response.data);
    },
    async fetchUsers(context, params) {
      return minsalAxios.get(`/users?${qs.stringify(params)}`)
        .then(response => response.data);
    },
    async deleteUser(context, user) {
      return minsalAxios.delete(`/users/${user.id}`)
        .then(response => response.data);
    },
    async removeOrganizationUser(context, { user, organization }) {
      return minsalAxios.post(`/organizations/${organization.uuid}/remove-user/${user.id}`);
    },
    async logout({ commit, dispatch }) {
      minsalAxios.defaults.headers.common['Authorization'] = null;
      commit('LOGOUT');
      dispatch('claveUnicaLogout');
    },
    async claveUnicaLogout() {
      window.location = `${CLAVEUNICA_LOGOUT_URL}?redirect=${encodeURIComponent(location.origin)}`;
    },
    async restoreUserSessionFromToken({ dispatch }, token) {
      // Set global Authorization header
      minsalAxios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return dispatch('getUserInfo', token)
        .then(response => {
          return dispatch('restoreUserSession', { user: response.data, token });
        })
        .catch(e => {
          dispatch('logout', e);
        });
    },
    async restoreUserSession({ commit }, payload) {
      commit('LOGIN', payload);
      return Promise.resolve(payload.user);
    },
    setWindowSize({ commit }, window) {
      commit('SET_WINDOW_SIZE', window);
    },
    async fetchOrganizations(context, params) {
      return minsalAxios.get(`/organizations?${qs.stringify(params)}`)
        .then(response => response.data);
    },
    async fetchOrganizationRemoteUsers(context, params) {
      return minsalAxios.get(`/organizations/remote-users?uuids=${params.uuids.join(',')}`)
        .then(response => response.data);
    },
    async deleteOrganization(context, organization) {
      return minsalAxios.delete(`/organizations/${organization.uuid}`)
        .then(response => response.data);
    },
    async saveOrganization({ commit }, formData) {
      return minsalAxios.post(`/organizations`, formData)
        .then(response => response.data);
    },
    async fetchOrganization(context, params) {
      return minsalAxios.get(`/organizations/${params.uuid}`)
        .then(response => response.data);
    },
    async fetchOrganizationUsers(context, params) {
      return minsalAxios.get(`/organizations/${params.uuid}/users?${qs.stringify(params)}`)
        .then(response => response.data);
    },
    async checkToken(context, payload) {
      return minsalAxios.get(`/check-token/${context.state.user.access_token}`);
    }
  },
  mutations: {
    LOGIN(state, { user, token }) {
      state.user = user;
      localStorage.setItem(`${LOCAL_STORE_PREFIX}-user-token`, token);
    },
    LOGOUT(state) {
      state.user = null;
      localStorage.removeItem(`${LOCAL_STORE_PREFIX}-user-token`);
    },
    SET_WINDOW_SIZE(state, window) {
      state.windowSize.innerWidth = window.innerWidth;
      state.windowSize.innerHeight = window.innerHeight;
    }
  }
});
